import React from 'react';

import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import metricsTokens from '@amzn/meridian-tokens/base/icon/metrics';
import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import viewTokens from '@amzn/meridian-tokens/base/icon/view';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { ICellRendererParams } from 'ag-grid-community';
import { EDITOR_ROLE_SET } from 'src/constants/role';
import { IRoyaltyCalculationRequest } from 'src/types/IRoyaltyCalculationRequest';

export const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return 'N/A';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
        // ISO date string
        try {
            const date = new Date(value);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short'
            });
        } catch {
            return value.toString();
        }
    }
    if (typeof value === 'number' && value.toString().length > 10) {
        // Likely a timestamp
        return new Date(value * 1000).toLocaleString();
    }
    return String(value) || 'N/A';
};

export const getPriorityLabel = (priority: number): string => {
    switch (priority) {
        case 1:
            return 'High (1)';
        case 2:
            return 'Medium (2)';
        case 3:
            return 'Low (3)';
        default:
            return `Priority ${priority}`;
    }
};

export const getStatusColor = (status: string): string => {
    const statusColors = {
        COMPLETED: '#4caf50',
        PENDING: '#ff9800',
        IN_PROGRESS: '#2196f3',
        FAILED: '#f44336',
        CANCELLED: '#9e9e9e',
        QUEUED: '#9c27b0'
    };
    return statusColors[status?.toUpperCase() as keyof typeof statusColors] || '#9e9e9e';
};

export const renderListValue = (items: string[] | undefined, maxDisplay = 5) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
        return (
            <Text type="b200" color="secondary">
                None specified
            </Text>
        );
    }

    const displayItems = items.slice(0, maxDisplay);
    const hasMore = items.length > maxDisplay;

    return (
        <Column spacing="100">
            {displayItems.map((item, index) => (
                <Text key={index} type="b200">
                    {item}
                </Text>
            ))}
            {hasMore && (
                <Text type="b200" color="secondary">
                    +{items.length - maxDisplay} more
                </Text>
            )}
        </Column>
    );
};

export const getBooleanCellRenderer = (params: ICellRendererParams) => {
    const value = params.value;
    if (value === undefined || value === null) return '';

    return (
        <span
            style={{
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: value ? '#4caf50' : '#9e9e9e',
                color: 'white',
                fontSize: '11px',
                fontWeight: 'bold'
            }}
        >
            {value ? 'Yes' : 'No'}
        </span>
    );
};

export const getStatusCellRenderer = (params: ICellRendererParams) => {
    const status = params.value;
    if (!status) return '';

    const statusColors = {
        COMPLETED: '#4caf50', // Green
        PENDING: '#ff9800', // Orange
        IN_PROGRESS: '#2196f3', // Blue
        FAILED: '#f44336', // Red
        CANCELLED: '#9e9e9e', // Gray
        QUEUED: '#9c27b0' // Purple
    };

    const backgroundColor = statusColors[status.toUpperCase() as keyof typeof statusColors] || '#9e9e9e';

    return (
        <span
            style={{
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: backgroundColor,
                color: 'white',
                fontSize: '11px',
                fontWeight: 'bold'
            }}
        >
            {status}
        </span>
    );
};

interface ActionsCellRendererProps extends ICellRendererParams {
    userInfo: { username: string };
    userRole: any;
    handleViewRequest: (id: number) => void;
    handleDetailRequest: (id: number) => void;
    handleEditRequest: (request: IRoyaltyCalculationRequest) => void;
    handleDeleteRequest: (request: IRoyaltyCalculationRequest) => void;
}

export const getActionsCellRenderer = (props: ActionsCellRendererProps) => {
    const { userInfo, userRole, handleViewRequest, handleDetailRequest, handleEditRequest, handleDeleteRequest } =
        props;
    const request = props.data as IRoyaltyCalculationRequest;
    const isOwner = request.requestedBy === userInfo.username;
    const isPending = request.statusName?.toUpperCase() === 'PENDING';
    const canModify = isOwner && EDITOR_ROLE_SET.has(userRole) && isPending;
    const isCompleted = request.statusName.toUpperCase() === 'COMPLETED';

    return (
        <Column height="100%" alignmentVertical="center">
            <Row spacing="200">
                {/* View button - always visible */}
                <Tooltip title="Request Details" position="top">
                    <Button
                        aria-label="view"
                        type="link"
                        size="small"
                        onClick={() => handleViewRequest(request.executionRequestId)}
                    >
                        <Icon className="button-icon" tokens={viewTokens} />
                    </Button>
                </Tooltip>

                {/* Grid button - navigate to execution requests detail page */}
                {isCompleted && (
                    <Tooltip title="Results" position="top">
                        <Button
                            label="view"
                            type="link"
                            size="small"
                            onClick={() => handleDetailRequest(request.executionRequestId)}
                        >
                            <Icon className="button-icon" tokens={metricsTokens} />
                        </Button>
                    </Tooltip>
                )}

                {/* Edit and Delete buttons - only for owners with pending requests */}
                {canModify && (
                    <>
                        <Button aria-label="edit" type="link" size="small" onClick={() => handleEditRequest(request)}>
                            <Icon className="button-icon" tokens={editTokens} />
                        </Button>
                        <Button
                            aria-label="delete"
                            type="link"
                            size="small"
                            onClick={() => handleDeleteRequest(request)}
                        >
                            <Icon className="button-icon" tokens={deleteTokens} />
                        </Button>
                    </>
                )}
            </Row>
        </Column>
    );
};
